import { useNavigate } from "react-router-dom"
import { MobileSidebar } from "@/components/mobile/MobileSidebar"
import { CreateModal } from "@/components/modals/CreateModal"
import { EditProfileTipsModal } from "@/components/modals/EditProfileTipsModal"
import { ReportModal } from "@/components/shared/ReportModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { NotificationsSheet } from "@/features/notifications/components/NotificationsSheet"
import { SearchOverlay } from "@/features/search/components/SearchOverlay"
import { useUIStore } from "@/store/uiStore"

interface LayoutModalsProps {
  notificationsOpen: boolean
  searchOpen: boolean
  createOpen: boolean
  shareOpen: boolean
  sidebarOpen: boolean
  reportModalOpen: boolean
  reportUserId: string | null
  reportCommunityModalOpen: boolean
  reportRitualModalOpen: boolean
  handleNotificationsOpen: (open: boolean) => void
  handleSearchOpen: (open: boolean) => void
  handleCreateOpen: (open: boolean) => void
  setShareOpen: (open: boolean) => void
  setSidebarOpen: (open: boolean) => void
  setReportModalOpen: (open: boolean) => void
  setReportCommunityModalOpen: (open: boolean) => void
  setReportRitualModalOpen: (open: boolean) => void
  shareUrl: string
  shareImageUrl?: string | null
  shareType?: "ritual" | "community" | "profile" | "coaching"
  isCommunityDetail: boolean
  isRitualDetail: boolean
  isEditProfilePage: boolean
  communityData:
    | {
        id: string
        name?: string
        handle?: string
        media?: Array<{ isCover?: boolean; filePath?: string }>
      }
    | undefined
  ritualUIData: { id?: string; title?: string; media?: Array<{ url?: string | null }> } | undefined
  otherProfileData:
    | { user: { id: string; name?: string; username?: string; profilePictureUrl?: string } }
    | undefined
}

export function LayoutModals({
  notificationsOpen,
  searchOpen,
  createOpen,
  shareOpen,
  sidebarOpen,
  reportModalOpen,
  reportUserId,
  reportCommunityModalOpen,
  reportRitualModalOpen,
  handleNotificationsOpen,
  handleSearchOpen,
  handleCreateOpen,
  setShareOpen,
  setSidebarOpen,
  setReportModalOpen,
  setReportCommunityModalOpen,
  setReportRitualModalOpen,
  shareUrl,
  shareImageUrl,
  shareType = "profile",
  isCommunityDetail,
  isRitualDetail,
  isEditProfilePage,
  communityData,
  ritualUIData,
  otherProfileData,
}: LayoutModalsProps) {
  const navigate = useNavigate()
  const { showEditProfileTipsModal, setShowEditProfileTipsModal } = useUIStore()
  return (
    <>
      <NotificationsSheet open={notificationsOpen} onOpenChange={handleNotificationsOpen} />
      <MobileSidebar open={sidebarOpen} onOpenChange={setSidebarOpen} />
      <SearchOverlay open={searchOpen} onOpenChange={handleSearchOpen} />
      <CreateModal
        isOpen={createOpen}
        onClose={() => handleCreateOpen(false)}
        onNavigate={path => navigate(path)}
      />
      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={shareUrl}
        shareImageUrl={shareImageUrl}
        shareType={shareType}
      />
      {!isEditProfilePage && (
        <EditProfileTipsModal
          isOpen={showEditProfileTipsModal}
          onClose={() => setShowEditProfileTipsModal(false)}
        />
      )}
      {otherProfileData && reportUserId && (
        <ReportModal
          open={reportModalOpen}
          onOpenChange={setReportModalOpen}
          target="USER"
          targetData={{
            id: otherProfileData.user.id,
            name: otherProfileData.user.name || "",
            handle: otherProfileData.user.username || "",
            imageUrl: otherProfileData.user.profilePictureUrl,
          }}
        />
      )}
      {isCommunityDetail && communityData && (
        <ReportModal
          open={reportCommunityModalOpen}
          onOpenChange={setReportCommunityModalOpen}
          target="COMMUNITY"
          targetData={{
            id: communityData.id,
            name: communityData.name || "",
            handle: communityData.handle || "",
            imageUrl:
              communityData.media?.find(m => m.isCover)?.filePath ||
              communityData.media?.[0]?.filePath ||
              null,
          }}
        />
      )}
      {isRitualDetail && ritualUIData && (
        <ReportModal
          open={reportRitualModalOpen}
          onOpenChange={setReportRitualModalOpen}
          target="RITUAL"
          targetData={{
            id: ritualUIData?.id || "",
            name: ritualUIData?.title || "",
            handle: "",
            imageUrl: ritualUIData?.media?.[0]?.url || null,
          }}
        />
      )}
    </>
  )
}
